import { MAIN_URL } from "./ServiceConstants";
import axios from "axios";
import LoginService from "./LoginServices";

function sqlString(str) {
  if (str === undefined || str === null) return "''";
  return `'${String(str).replace(/'/g, "''")}'`;
}

async function encryptQuery(plainText, password) {
  if (plainText === null) return null;
  const pw = password || "";
  
  const encoder = new TextEncoder();
  const plainTextBytes = encoder.encode(plainText);
  const passwordBytes = encoder.encode(pw);

  // Hash password bytes using SHA-256
  const sha256Buffer = await window.crypto.subtle.digest("SHA-256", passwordBytes);
  const sha256Bytes = new Uint8Array(sha256Buffer);

  // Import key for PBKDF2
  const baseKey = await window.crypto.subtle.importKey(
    "raw",
    sha256Bytes,
    "PBKDF2",
    false,
    ["deriveBits"]
  );

  // Derive bits (48 bytes: 32 for key, 16 for IV)
  const saltBytes = new Uint8Array([1, 2, 3, 4, 5, 6, 7, 8]);
  const derivedBitsBuffer = await window.crypto.subtle.deriveBits(
    {
      name: "PBKDF2",
      salt: saltBytes,
      iterations: 1000,
      hash: "SHA-1"
    },
    baseKey,
    384 // 48 bytes * 8 bits/byte = 384 bits
  );
  
  const derivedBits = new Uint8Array(derivedBitsBuffer);
  const keyBytes = derivedBits.slice(0, 32);
  const ivBytes = derivedBits.slice(32, 48);

  // Import AES key
  const aesKey = await window.crypto.subtle.importKey(
    "raw",
    keyBytes,
    "AES-CBC",
    false,
    ["encrypt"]
  );

  // Encrypt with AES-CBC
  const encryptedBuffer = await window.crypto.subtle.encrypt(
    {
      name: "AES-CBC",
      iv: ivBytes
    },
    aesKey,
    plainTextBytes
  );

  // Convert to Base64
  const encryptedBytes = new Uint8Array(encryptedBuffer);
  let binary = "";
  for (let i = 0; i < encryptedBytes.byteLength; i++) {
    binary += String.fromCharCode(encryptedBytes[i]);
  }
  return window.btoa(binary);
}

async function runQuery(queryText) {
  const user = LoginService.getUser();
  const SessionGUID = user ? user.SessionGUID : "";
  const sequence = sessionStorage.getItem("sequence", "0");
  const nextSequence = Number(sequence || "0") + 1;
  sessionStorage.setItem("sequence", nextSequence);

  const pp = `${SessionGUID}${nextSequence}`;

  const encryptedQuery = await encryptQuery(queryText, pp);

  const url = MAIN_URL + "/OrthoMagic/GetQueryResult";
  const dataObject = {
    Query: encryptedQuery,
  };
  return axios.post(url, { dataObject, sequence: nextSequence });
}

async function getJobFullDetailsByPatientCode(patientCode) {
  const codeSql = sqlString(patientCode);
  const query = `
    SELECT
      i.Id AS InstanceId,
      i.InstanceType,
      i.Note AS InstanceNote,
      i.DueDate,
      p.Id AS PatientId,
      p.FirstName AS PatientFirstName,
      p.FamilyName AS PatientFamilyName,
      p.PatientCode,
      d.FullName AS DoctorName,
      a.Id AS ActionId,
      a.ActionStep,
      a.Note AS ActionNote,
      a.SystemDate AS ActionStartDate,
      s.Dsc AS StepDescription
    FROM tbl_Instance i
    LEFT JOIN tbl_PATIENT p ON i.PatientId = p.Id
    LEFT JOIN tbl_DOCTOR d ON p.DoctorId = d.Id
    LEFT JOIN tbl_Action a ON a.InstanceId = i.Id AND a.CompletedAt IS NULL
    LEFT JOIN tbl_ActionSteps s ON a.ActionStep = s.ActionStep AND i.InstanceType = s.InstanceType
    WHERE p.PatientCode = ${codeSql}
      AND i.Id = (
        SELECT TOP 1 sub_i.Id
        FROM tbl_Instance sub_i
        JOIN tbl_PATIENT sub_p ON sub_i.PatientId = sub_p.Id
        WHERE sub_p.PatientCode = ${codeSql}
        ORDER BY sub_i.SystemDate DESC
      )
  `;
  return runQuery(query);
}

async function getPossibleActions(actionId) {
  const user = LoginService.getUser();
  const userId = user ? user.UserId : 0;
  const query = `exec sel_PossibleActions ${userId}, ${actionId}`;
  return runQuery(query);
}

async function completeAction(actionId, actionStep, note) {
  const user = LoginService.getUser();
  const userId = user ? user.UserId : 0;
  const noteSql = sqlString(note);
  const query = `exec up_ActionComplete ${userId}, ${actionId}, ${actionStep}, ${noteSql}, 0, ''`;
  return runQuery(query);
}

const WorkService = {
  getJobFullDetailsByPatientCode,
  getPossibleActions,
  completeAction,
  runQuery,
};

export default WorkService;
